import { useEffect, useMemo } from 'react';
import { hasDraftContent, signatureForCompare } from '../../utils/productSelectionSendDraft';

const LOCAL_CACHE_TTL_MS = 5 * 60 * 1000;

const getBrowserStorage = () => {
  if (typeof window === 'undefined') return null;
  const storage = window.localStorage;
  if (!storage) return null;
  if (
    typeof storage.getItem !== 'function' ||
    typeof storage.setItem !== 'function' ||
    typeof storage.removeItem !== 'function'
  ) {
    return null;
  }
  return storage;
};

const useSendTabDraft = ({ storageKey, formValues, setFormValues, baselineSignatureRef }) => {
  const hasStorageKey = useMemo(() => Boolean(storageKey), [storageKey]);

  useEffect(() => {
    const storage = getBrowserStorage();
    if (!hasStorageKey || !storage) return;
    try {
      const raw = storage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.values || !hasDraftContent(parsed.values)) {
        storage.removeItem(storageKey);
        return;
      }
      const age = Date.now() - (parsed.savedAt || 0);
      if (age > LOCAL_CACHE_TTL_MS) {
        storage.removeItem(storageKey);
        return;
      }
      setFormValues(parsed.values);
      console.info('[SendSelection] restored draft from storage', storageKey);
    } catch (err) {
      console.warn('[SendSelection] failed to restore draft', err);
      try {
        storage.removeItem(storageKey);
      } catch {
        // Ignore follow-up storage failures after the primary restore error.
      }
    }
  }, [hasStorageKey, setFormValues, storageKey]);

  useEffect(() => {
    const storage = getBrowserStorage();
    if (!hasStorageKey || !storage) return;
    if (!hasDraftContent(formValues)) return;
    const baselineSignature = baselineSignatureRef?.current || '';
    const currentSignature = signatureForCompare(formValues);
    const isDirty = Boolean(baselineSignature) && baselineSignature !== currentSignature;
    if (!isDirty) return;
    try {
      storage.setItem(storageKey, JSON.stringify({ values: formValues, savedAt: Date.now() }));
    } catch (err) {
      console.warn('[SendSelection] failed to persist draft', err);
    }
  }, [baselineSignatureRef, formValues, hasStorageKey, storageKey]);

  const clearDraft = () => {
    const storage = getBrowserStorage();
    if (!hasStorageKey || !storage) return;
    try {
      storage.removeItem(storageKey);
    } catch (err) {
      console.warn('[SendSelection] failed to clear draft', err);
    }
  };

  return { clearDraft };
};

export default useSendTabDraft;
